import React from 'react';
import { CheckCircle, AlertCircle, Clock, Eye, RefreshCw, Plus, Ticket as TicketIcon, AlertTriangle } from 'lucide-react';
import { useSupportPage } from './useSupportPage';
import { SupportNotificationModal } from './SupportNotificationModal';
import { SupportTicketFormModal } from './SupportTicketFormModal';

const getStatusBadge = (status: string) => {
  const badges = {
    open: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', icon: Clock },
    in_progress: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: Eye },
    resolved: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', icon: CheckCircle },
    closed: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: CheckCircle }
  };
  return badges[status as keyof typeof badges] || badges.open;
};

const getPriorityBadge = (priority: string) => {
  const badges = {
    low: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: Clock },
    medium: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: Clock },
    high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', icon: AlertCircle },
    urgent: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: AlertTriangle }
  };
  return badges[priority as keyof typeof badges] || badges.medium;
};

const SupportPage: React.FC = () => {
  const {
    ticketFormData,
    setTicketFormData,
    customIssueType,
    setCustomIssueType,
    ticketImagePreviews,
    ticketImageNames,
    uploadingTicketImage,
    ticketLoading,
    loadingTickets,
    tickets,
    showTicketForm,
    setShowTicketForm,
    ticketMessage,
    setTicketMessage,
    loadTickets,
    getFilenameFromUrl,
    handleTicketImageChange,
    removeTicketImage,
    handleTicketSubmit,
    closeTicketForm,
    formatIssueType,
  } = useSupportPage();

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 -top-32 w-[400px] h-[400px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 -bottom-32 w-[400px] h-[400px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      <div className="relative px-4 lg:px-6 py-4 lg:py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-50 mb-1">
            Support
          </h1>
          <p className="text-sm text-gray-400">Raise tickets for support and assistance</p>
        </div>

        {ticketMessage && (
          <SupportNotificationModal
            message={ticketMessage}
            onClose={() => setTicketMessage(null)}
          />
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-white">My Tickets ({tickets.length})</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={loadTickets}
                disabled={loadingTickets}
                className="p-1 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loadingTickets ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowTicketForm(!showTicketForm)}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/20"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="font-medium text-xs">New Ticket</span>
              </button>
            </div>
          </div>

          <SupportTicketFormModal
            open={showTicketForm}
            formData={ticketFormData}
            setFormData={setTicketFormData}
            customIssueType={customIssueType}
            setCustomIssueType={setCustomIssueType}
            imagePreviews={ticketImagePreviews}
            imageNames={ticketImageNames}
            getFilenameFromUrl={getFilenameFromUrl}
            onImageChange={handleTicketImageChange}
            onRemoveImage={removeTicketImage}
            onSubmit={handleTicketSubmit}
            onClose={closeTicketForm}
            loading={ticketLoading}
            uploadingImage={uploadingTicketImage}
          />

          {loadingTickets ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-lg p-8 text-center">
              <TicketIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-400 text-xs">No tickets raised yet</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {tickets.map((ticket) => {
                const statusBadge = getStatusBadge(ticket.status);
                const priorityBadge = getPriorityBadge(ticket.priority);
                const PriorityIcon = priorityBadge.icon;
                return (
                  <div
                    key={ticket.id}
                    className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-lg p-3 hover:border-cyan-500/30 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-white truncate group-hover:text-cyan-400 transition-colors">
                              {ticket.title}
                            </h3>
                            <div className="flex items-center space-x-1.5 mt-0.5">
                              {ticket.issue_type && (
                                <span className="text-xs text-gray-400">{formatIssueType(ticket.issue_type)}</span>
                              )}
                              <span className="text-xs text-gray-400">
                                • {new Date(ticket.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0 ml-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium border flex items-center space-x-1 ${priorityBadge.bg} ${priorityBadge.text} ${priorityBadge.border}`}>
                              <PriorityIcon className="w-2.5 h-2.5" />
                              <span className="capitalize">{ticket.priority}</span>
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}>
                              {ticket.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-300 mb-2 line-clamp-2">{ticket.description}</p>
                        {ticket.image_url && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {ticket.image_url.split(',')
                              .map(url => url.trim())
                              .filter(url => url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api/')))
                              .map((url, index) => {
                                const imageUrl = url.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url;
                                const fileName = getFilenameFromUrl(imageUrl);
                                return (
                                  <div key={index} className="inline-block">
                                    <img
                                      src={imageUrl}
                                      alt={`Ticket attachment ${index + 1}`}
                                      className="max-w-[200px] h-auto rounded border border-gray-700/50 max-h-24 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                    <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px]" title={fileName}>{fileName}</p>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                        {ticket.admin_response && (
                          <div className="mt-2 pt-2 border-t border-gray-700/50">
                            <div className="flex items-center space-x-1 mb-0.5">
                              <CheckCircle className="w-2.5 h-2.5 text-cyan-400" />
                              <span className="text-xs text-cyan-400">Admin Response:</span>
                            </div>
                            <p className="text-xs text-gray-300 line-clamp-2">{ticket.admin_response}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
